import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { usePresence, areaToChannel } from "@/context/PresenceContext";
import { ChatPanel } from "@/components/ChatPanel";
import { BettableNow } from "@/components/BettableNow";
import { OpenMatches } from "@/components/OpenMatches";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { TradeQuickRequest } from "@/components/TradeQuickRequest";
import { FloatingChatFrame, CHAT_DRAG_HANDLE } from "@/components/FloatingChatFrame";
import { ChatHistoryClear } from "@/components/ChatHistoryClear";
import { MessageCircle, X, Globe, Repeat, Coins, Swords, Skull, Flag, Sparkles, Plus, GripHorizontal, RotateCcw } from "lucide-react";

const OPACITY_KEY = "playden_chat_opacity";

const CHANNELS = [
  { key: "global", label: "General", icon: Globe },
  { key: "trade", label: "Trade", icon: Repeat },
  { key: "bet", label: "Predict", icon: Coins },
  { key: "arena", label: "Arena", icon: Swords },
  { key: "cavern", label: "Cavern", icon: Skull },
  { key: "race", label: "Race", icon: Flag },
];

export function GlobalChat() {
  const { user } = useAuth();
  const { area, count, label } = usePresence();
  const [open, setOpen] = useState(false);
  const [channel, setChannel] = useState("global");
  const [credits, setCredits] = useState(user?.credits ?? 0);
  const [sp, setSp] = useState(user?.sp ?? 0);

  // Balance mirrors AuthContext.user, which refreshes instantly after any
  // action, on tab focus, and on a slow safety poll — no own /auth/me poll.
  useEffect(() => { setCredits(user?.credits ?? 0); setSp(user?.sp ?? 0); }, [user?.credits, user?.sp]);

  // When you land on a page that has its own chat room, jump the chat to it.
  useEffect(() => {
    const ch = areaToChannel(area);
    if (ch) setChannel(ch);
  }, [area]);
  // Non-sensitive display preferences only; never credentials or authorization.
  const [opacity, setOpacity] = useState(() => {
    const v = Number(localStorage.getItem(OPACITY_KEY));
    return v >= 20 && v <= 100 ? v : 60;
  });
  const [quickReqOpen, setQuickReqOpen] = useState(false);
  if (!user) return null;

  const saveOpacity = (v) => {
    const val = Array.isArray(v) ? v[0] : v;
    setOpacity(val);
    localStorage.setItem(OPACITY_KEY, String(val));
  };

  return (
    <>
      {/* Floating balance chip — sits right above the chat bubble so users
       *  can always see their credits + SP while browsing (esp. on the
       *  Store, where the sticky header hides on scroll). Tapping it
       *  toggles the chat, matching the bubble. */}
      {!open && (
        <button
          data-testid="chat-balance-chip"
          onClick={() => setOpen(true)}
          className="fixed bottom-[5.25rem] left-5 z-40 hud-pill flex items-center gap-2 rounded-full bg-black/70 backdrop-blur border border-white/15 px-3 py-1.5 shadow-lg hover:border-cyan-300/40 transition-colors"
          aria-label={`Balance: ${credits} credits, ${sp} SP`}
        >
          <span className="flex items-center gap-1" data-testid="chat-chip-credits">
            <Coins className="h-3.5 w-3.5 text-amber-400" />
            <span className="font-mono text-[11px] text-white tabular-nums">{credits.toLocaleString()}</span>
          </span>
          <span className="h-3 w-px bg-white/15" />
          <span className="flex items-center gap-1" data-testid="chat-chip-sp">
            <Sparkles className="h-3.5 w-3.5 text-fuchsia-400" />
            <span className="font-mono text-[11px] text-white tabular-nums">{sp.toLocaleString()}</span>
          </span>
        </button>
      )}

      <button
        data-testid="global-chat-toggle"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-5 left-5 z-40 h-14 w-14 rounded-full bg-cyan-400 text-black shadow-[0_10px_40px_rgba(0,220,255,0.4)] flex items-center justify-center hover:bg-cyan-300 transition-colors"
        aria-label="Lobby chat"
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>

      {open && (
        <FloatingChatFrame key={user.id} testId="global-chat-window" storageKey={`playden_chat_frame_v1:${user.id}`}>
        {({ rect, resizeTo, reset, moveKeys }) => <div className="h-full min-h-0 flex flex-col overflow-hidden rounded-2xl border border-white/10 backdrop-blur-md shadow-2xl" style={{ backgroundColor: `rgba(14, 18, 28, ${opacity / 100})` }}>
          <div className="flex items-center gap-1 px-2 py-2 border-b border-white/10 shrink-0">
            <button type="button" data-testid="chat-drag-handle" title="Move chat" aria-label="Move chat" onKeyDown={moveKeys} className={`${CHAT_DRAG_HANDLE} flex min-w-0 items-center gap-1.5 cursor-grab active:cursor-grabbing touch-none select-none py-1.5 text-white`}>
              <GripHorizontal className="h-4 w-4 shrink-0 text-cyan-300" /><span className="font-display text-sm whitespace-nowrap">LOBBY CHAT</span>
            </button>
            <span data-testid="chat-here-count" className="ml-auto min-w-0 inline-flex items-center gap-1 rounded-full bg-emerald-400/10 border border-emerald-400/30 px-1.5 py-0.5 font-mono text-[9px] text-emerald-300">
              <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" /><span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" /></span>
              <span className="truncate">{count} in {label}</span>
            </span>
            <button data-testid="chat-reset-position" type="button" title="Reset chat size and position" aria-label="Reset chat size and position" onClick={reset} className="p-1.5 shrink-0 text-white/50 hover:text-cyan-300"><RotateCcw className="h-3.5 w-3.5" /></button>
            <ChatHistoryClear testId="global-chat-clear-history" endpoint={`/chat/${encodeURIComponent(channel)}/clear`} queryKey={["chat", channel]} />
            <button data-testid="chat-window-close" type="button" title="Close chat" aria-label="Close chat" onClick={() => setOpen(false)} className="p-1.5 shrink-0 text-white/60 hover:text-white"><X className="h-4 w-4" /></button>
          </div>

          {/* Channel tabs */}
          <div className="flex flex-wrap gap-1 px-3 pt-2 shrink-0" data-testid="chat-channels">
            {CHANNELS.map((c) => {
              const active = channel === c.key;
              return (
                <button
                  key={c.key}
                  data-testid={`chat-channel-${c.key}`}
                  onClick={() => setChannel(c.key)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-t-lg text-xs font-medium transition-colors ${active ? "bg-white/10 text-white" : "text-white/45 hover:text-white/80"}`}
                >
                  <c.icon className="h-3.5 w-3.5" /> {c.label}
                </button>
              );
            })}
          </div>

          {/* Sliders */}
          <div className="flex items-center gap-3 px-3 py-2 border-y border-white/5 shrink-0">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/40 shrink-0">Size</span>
              <Slider data-testid="chat-size-slider" aria-label="Chat size" value={[Math.round(Math.max(0, Math.min(100, (rect.width - 320) / 3.2)))]} min={0} max={100} step={5} onValueChange={([value]) => resizeTo({ width: 320 + value * 3.2, height: 460 + value * 2.6 })} className="flex-1" />
            </div>
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/40 shrink-0">Opacity</span>
              <Slider data-testid="chat-opacity-slider" value={[opacity]} min={20} max={100} step={5} onValueChange={saveOpacity} className="flex-1" />
              <span className="font-mono text-[10px] text-white/50 w-8 text-right tabular-nums" data-testid="chat-opacity-value">{opacity}%</span>
            </div>
          </div>

          <div className="px-3 py-3 flex flex-col flex-1 min-h-0 min-w-0 overflow-hidden">
            <div className="shrink-0 max-h-[35%] overflow-y-auto min-w-0">
            {channel === "bet" && <BettableNow />}
            {["arena", "cavern", "race"].includes(channel) && <OpenMatches kind={channel} />}
            {channel === "trade" && (
              <div className="mb-2 flex flex-wrap items-center gap-2" data-testid="chat-trade-actions">
                <Button
                  size="sm"
                  onClick={() => setQuickReqOpen(true)}
                  data-testid="chat-open-quick-request"
                  className="bg-cyan-400 text-black hover:bg-cyan-300 h-8 px-3 text-xs font-medium"
                >
                  <Plus className="h-3.5 w-3.5 mr-1" /> New trade request
                </Button>
                <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-white/40">
                  Give / Want · specific card or &quot;any&quot;
                </span>
              </div>
            )}
            </div>
            <ChatPanel key={channel} channel={channel} testidPrefix="global-chat" fillHeight lfgKind={["arena", "cavern", "race"].includes(channel) ? channel : null} />
          </div>

          {/* Balance strip — kept visible while chat is open so players can
           *  glance at credits/SP without closing the panel. */}
          <div className="flex flex-wrap items-center justify-center gap-3 px-3 py-2 border-t border-white/10 bg-black/30 shrink-0" data-testid="chat-window-balance">
            <div className="flex items-center gap-1.5" data-testid="chat-window-credits">
              <Coins className="h-3.5 w-3.5 text-amber-400" />
              <span className="font-mono text-[11px] text-white tabular-nums">{credits.toLocaleString()}</span>
              <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-white/40">Credits</span>
            </div>
            <span className="h-3 w-px bg-white/15" />
            <div className="flex items-center gap-1.5" data-testid="chat-window-sp">
              <Sparkles className="h-3.5 w-3.5 text-fuchsia-400" />
              <span className="font-mono text-[11px] text-white tabular-nums">{sp.toLocaleString()}</span>
              <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-white/40">SP</span>
            </div>
          </div>
        </div>}
        </FloatingChatFrame>
      )}

      <TradeQuickRequest
        channel="trade"
        open={quickReqOpen}
        onOpenChange={setQuickReqOpen}
      />
    </>
  );
}
